import type { DeepResearchStepProps } from './DeepResearchStep';
import type { ModuleReportData, GuideStructureData } from '../../providers/DeepSearchCompletion';
import { useEffect, useState, useRef, useCallback } from 'react';
import { DeepSearchCompletionType, useDeepSearchCompletion } from '../../providers/DeepSearchCompletion';
import { DeepResearchStep, DeepResearchStepStatus } from './DeepResearchStep';

export const DeepResearchStepperMessage = ({
  requestId,
  onComplete,
}: {
  requestId: string;
  onComplete: () => void;
}) => {
  const [indexModuleReport, setIndexModuleReport] = useState<number>(0);
  const [moduleCount, setModuleCount] = useState<number>(0);
  const { messagesByRequestId } = useDeepSearchCompletion();

  // Use refs to access current state values in the subscription callback
  // without adding them as dependencies to the useEffect
  const indexModuleReportRef = useRef(indexModuleReport);
  const moduleCountRef = useRef(moduleCount);

  const requestIdRef = useRef(requestId);
  requestIdRef.current = requestId;

  // Keep refs in sync with state
  useEffect(() => {
    indexModuleReportRef.current = indexModuleReport;
  }, [indexModuleReport]);

  useEffect(() => {
    moduleCountRef.current = moduleCount;
  }, [moduleCount]);

  const [steps, setSteps] = useState<DeepResearchStepProps[]>([
    { slug: 'guide_structure', label: 'Generating Guide Structure', status: DeepResearchStepStatus.PENDING },
    { slug: 'module_reports', label: 'Generating Module Reports', status: DeepResearchStepStatus.IDLE },
  ]);

  const handleCompleted = useCallback(
    (data: ModuleReportData | GuideStructureData) => {
      if (data.type === DeepSearchCompletionType.GUIDE_STRUCTURE) {
        const moduleSteps = (data as GuideStructureData).modules.map((module, index) => ({
          slug: `module_${index}`,
          label: module.moduleTitle,
          status: index === 0 ? DeepResearchStepStatus.PENDING : DeepResearchStepStatus.IDLE,
        }));
        setModuleCount(moduleSteps.length);

        // check if the module steps are already in the steps array

        setSteps((prev) => {
          const newSteps = [...prev];
          const moduleStepsInSteps = prev.some((step) =>
            moduleSteps.some((moduleStep) => moduleStep.slug === step.slug),
          );
          newSteps[0].status = DeepResearchStepStatus.COMPLETED;
          newSteps[1].status = DeepResearchStepStatus.PENDING;
          if (!moduleStepsInSteps) {
            return [...newSteps, ...moduleSteps];
          }
          return newSteps;
        });
      }
      if (data.type === DeepSearchCompletionType.MODULE_REPORT) {
        setSteps((prev) => {
          const newSteps = [...prev];
          // Use the ref value instead of the state directly

          // Use the ref values for comparison
          if (indexModuleReportRef.current === moduleCountRef.current - 1) {
            newSteps[1].status = DeepResearchStepStatus.COMPLETED;
            onComplete();
          } else {
            // find the step with the same slug as the module report
            setIndexModuleReport((prevIndex) => prevIndex + 1);
          }
          const stepIndex = newSteps.findIndex((step) => step.slug === `module_${indexModuleReportRef.current}`);
          newSteps[stepIndex].status = DeepResearchStepStatus.COMPLETED;
          // if there is a next step, set it to pending
          if (stepIndex + 1 < newSteps.length) {
            newSteps[stepIndex + 1].status = DeepResearchStepStatus.PENDING;
          }

          return newSteps;
        });
      }
    },
    [onComplete],
  );

  useEffect(() => {
    const messages = messagesByRequestId.get(requestId);
    if (messages && messages[messages.length - 1].status === 'completed') {
      handleCompleted(messages[messages.length - 1].data);
    }
  }, [messagesByRequestId, requestId, handleCompleted]);

  return (
    <div>
      {steps.map((step) => (
        <DeepResearchStep key={step.label} {...step} />
      ))}
    </div>
  );
};
